import React, {useCallback} from 'react';

import {LimitItem} from 'src/components/limit-item/limit-item';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {MAX_NUMBER_VALUE} from 'src/constants';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useSearchLimits} from 'src/hooks/dictionaries';

import {FormSection} from '@pb/kit-component-form-section';

import {UserSearchLimitsSkeleton} from './user-search-limits.skeleton';

import type {Dispatch, SetStateAction} from 'react';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Limit} from 'src/openapi/admin/models/Limit';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    disabled?: boolean,

    usedLimits?: Limit[],
    defaultSearchLimits: Limit[] | null,
    userSearchLimits: Limit[],
    setUserSearchLimits: Dispatch<SetStateAction<Limit[]>>,
    onFieldChange: (fieldName: string) => void,
};

export const UserSearchLimitsView = (props: Props) => {
    const {
        isLoading,
        loadingError,
        disabled,
        onFieldChange,

        usedLimits,
        defaultSearchLimits,
        userSearchLimits,
        setUserSearchLimits,
    } = props;
    const {searchLimits} = useSearchLimits();

    const changeSearchLimit = useCallback((id: string, newValue?: number) => {
        if (id) {
            const defaultValue = defaultSearchLimits?.find(({limitId}) => (
                id === limitId
            ))?.value;

            setUserSearchLimits(prevState => {
                if (prevState.find(e => e.limitId === id)?.value !== newValue) {
                    onFieldChange('Search: ' + id);
                }

                const unlimited = newValue === undefined;
                const value = unlimited ? MAX_NUMBER_VALUE : newValue;

                return [
                    ...prevState.filter(({limitId}) => id !== limitId),
                    ...defaultValue === value ? [] : [{limitId: id, value, unlimited}],
                ];
            });
        }
    }, [defaultSearchLimits, onFieldChange, setUserSearchLimits]);

    const clearCustomLimit = useCallback((id: string) => {
        setUserSearchLimits(prevState => {
            onFieldChange('Search: ' + id);
            return prevState.filter(({limitId}) => id !== limitId);
        });
    }, [onFieldChange, setUserSearchLimits]);

    return (
        <Skeleton shown={isLoading} view={<UserSearchLimitsSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                accessRight={ACCESS_RIGHTS.USER_LIMITS_VIEW as AccessRight}>
                <FormSection headline="Search results pages">
                    <ResponsiveElementGroup>
                        {searchLimits.map(({description, code}) => {
                            const defaultValue = defaultSearchLimits?.find(({limitId}) => (
                                code === limitId
                            ))?.value || 0;

                            const customValue = userSearchLimits.find(({limitId}) => (
                                code === limitId
                            ))?.value;

                            const usedValue = usedLimits?.find(({limitId}) => (
                                code === limitId
                            ))?.value;

                            return (
                                <WidthItem key={code} width={300}>
                                    <LimitItem
                                        key={code}
                                        limitId={code}
                                        readonly={disabled}
                                        limitDictionary={{code, description}}
                                        defaultValue={defaultValue}
                                        customValue={customValue}
                                        usedValue={usedValue}
                                        onChangeLimit={changeSearchLimit}
                                        onClearCustomLimit={clearCustomLimit}
                                    />
                                </WidthItem>
                            );
                        })}
                    </ResponsiveElementGroup>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
